import { argon2id } from "hash-wasm";
import {
  CLIENT_KDF_DEFAULTS,
  RECOVERY_KDF_DEFAULTS,
  bytesToArrayBuffer,
  canonicalJson,
  descriptorPayload,
  didFromPublicJwk,
  fromBase64Url,
  randomBase64Url,
  textDecoder,
  textEncoder,
  toBase64Url,
  type DescriptorEvent,
  type KdfParameters,
  type SignedDescriptorEvent,
  type WrappedEnvelope,
} from "../shared/protocol";

const HKDF_SALT = textEncoder.encode("vibe-cloud-hkdf-v1");

export interface AccountCrypto {
  rootDid: string;
  publicJwk: JsonWebKey;
  privateJwk: JsonWebKey;
  dek: Uint8Array;
  kdf: KdfParameters;
  authSecret: string;
  wrappedRoot: WrappedEnvelope;
  wrappedDek: WrappedEnvelope;
  descriptor: SignedDescriptorEvent;
}

export async function createAccountCrypto(password: string, nodeEndpoint: string): Promise<AccountCrypto> {
  const keyPair = (await crypto.subtle.generateKey(
    { name: "ECDSA", namedCurve: "P-256" },
    true,
    ["sign", "verify"],
  )) as CryptoKeyPair;
  const publicJwk = await crypto.subtle.exportKey("jwk", keyPair.publicKey);
  const privateJwk = await crypto.subtle.exportKey("jwk", keyPair.privateKey);
  const rootDid = didFromPublicJwk(publicJwk);
  const dek = crypto.getRandomValues(new Uint8Array(32));
  const kdf = newKdfParameters(CLIENT_KDF_DEFAULTS);
  const { wrappingKey, authSecret } = await derivePasswordKeys(password, kdf);
  const wrappedRoot = await wrapBytes(
    wrappingKey,
    textEncoder.encode(canonicalJson(privateJwk as never)),
    "vibe:root-private-key:v1",
  );
  const wrappedDek = await wrapBytes(wrappingKey, dek, "vibe:vault-dek:v1");
  const payload: DescriptorEvent = {
    version: 1,
    rootDid,
    sequence: 0,
    previousEventHash: null,
    nodeEndpoint,
    operationalKeys: [],
    issuedAt: new Date().toISOString(),
    supersedesEndpoint: null,
  };
  const signature = await crypto.subtle.sign(
    { name: "ECDSA", hash: "SHA-256" },
    keyPair.privateKey,
    textEncoder.encode(canonicalJson(payload as never)),
  );
  const signatureBytes = new Uint8Array(signature);
  if (signatureBytes.length !== 64) throw new Error("Browser returned a non-P1363 P-256 signature.");
  const descriptor: SignedDescriptorEvent = {
    ...payload,
    signature: { algorithm: "ES256", encoding: "p1363", value: toBase64Url(signatureBytes) },
  };

  return { rootDid, publicJwk, privateJwk, dek, kdf, authSecret, wrappedRoot, wrappedDek, descriptor };
}

export async function derivePasswordKeys(password: string, parameters: KdfParameters): Promise<{
  wrappingKey: CryptoKey;
  authSecret: string;
}> {
  const base = await deriveArgon(password, parameters);
  const hkdfKey = await crypto.subtle.importKey("raw", bytesToArrayBuffer(base), "HKDF", false, ["deriveKey", "deriveBits"]);
  base.fill(0);
  const wrappingKey = await crypto.subtle.deriveKey(
    { name: "HKDF", hash: "SHA-256", salt: HKDF_SALT, info: textEncoder.encode("vibe-wrap-v1") },
    hkdfKey,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
  const authBits = await crypto.subtle.deriveBits(
    { name: "HKDF", hash: "SHA-256", salt: HKDF_SALT, info: textEncoder.encode("vibe-auth-v1") },
    hkdfKey,
    256,
  );
  return { wrappingKey, authSecret: toBase64Url(new Uint8Array(authBits)) };
}

export async function unlockEnvelopes(
  password: string,
  kdf: KdfParameters,
  wrappedRoot: WrappedEnvelope,
  wrappedDek: WrappedEnvelope,
): Promise<{ privateJwk: JsonWebKey; dek: Uint8Array }> {
  const { wrappingKey } = await derivePasswordKeys(password, kdf);
  const rootBytes = await unwrapBytes(wrappingKey, wrappedRoot, "vibe:root-private-key:v1");
  const dek = await unwrapBytes(wrappingKey, wrappedDek, "vibe:vault-dek:v1");
  return { privateJwk: JSON.parse(textDecoder.decode(rootBytes)) as JsonWebKey, dek };
}

export async function makeRecoveryKit(input: {
  rootDid: string;
  privateJwk: JsonWebKey;
  dek: Uint8Array;
  descriptor: SignedDescriptorEvent;
}): Promise<{ code: string; kit: Record<string, unknown>; recoveryKdf: KdfParameters; wrappedRoot: WrappedEnvelope; wrappedDek: WrappedEnvelope }> {
  const code = formatRecoveryCode(crypto.getRandomValues(new Uint8Array(20)));
  const recoveryKdf = newKdfParameters(RECOVERY_KDF_DEFAULTS);
  const recoveryBase = await deriveArgon(code.replaceAll("-", ""), recoveryKdf);
  const recoveryKey = await crypto.subtle.importKey("raw", bytesToArrayBuffer(recoveryBase), { name: "AES-GCM" }, false, ["encrypt", "decrypt"]);
  recoveryBase.fill(0);
  const wrappedRoot = await wrapBytes(
    recoveryKey,
    textEncoder.encode(canonicalJson(input.privateJwk as never)),
    "vibe:recovery-root:v1",
  );
  const wrappedDek = await wrapBytes(recoveryKey, input.dek, "vibe:recovery-dek:v1");
  const kit = {
    kind: "vibe-recovery-kit",
    version: 1,
    rootDid: input.rootDid,
    recoveryKdf,
    wrappedRoot,
    wrappedDek,
    descriptorCheckpoint: input.descriptor,
    notice: "This encrypted file needs the separately stored recovery code. It contains authority keys, not your data backup.",
  };
  return { code, kit, recoveryKdf, wrappedRoot, wrappedDek };
}

export async function encryptVaultValue(dek: Uint8Array, value: unknown): Promise<WrappedEnvelope> {
  const key = await crypto.subtle.importKey("raw", bytesToArrayBuffer(dek), { name: "AES-GCM" }, false, ["encrypt"]);
  return wrapBytes(key, textEncoder.encode(JSON.stringify(value)), "vibe:vault-item:v1");
}

export async function decryptVaultValue<T>(dek: Uint8Array, envelope: WrappedEnvelope): Promise<T> {
  const key = await crypto.subtle.importKey("raw", bytesToArrayBuffer(dek), { name: "AES-GCM" }, false, ["decrypt"]);
  const clear = await unwrapBytes(key, envelope, "vibe:vault-item:v1");
  return JSON.parse(textDecoder.decode(clear)) as T;
}

async function deriveArgon(password: string, parameters: KdfParameters): Promise<Uint8Array> {
  const result = await argon2id({
    password: password.normalize("NFKC"),
    salt: fromBase64Url(parameters.salt),
    parallelism: parameters.parallelism,
    iterations: parameters.iterations,
    memorySize: parameters.memorySize,
    hashLength: parameters.hashLength,
    outputType: "binary",
  });
  return result as Uint8Array;
}

function newKdfParameters(defaults: typeof CLIENT_KDF_DEFAULTS): KdfParameters {
  return {
    algorithm: "argon2id",
    version: 1,
    salt: randomBase64Url(16),
    ...defaults,
  };
}

async function wrapBytes(key: CryptoKey, cleartext: Uint8Array, aad: string): Promise<WrappedEnvelope> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv, additionalData: textEncoder.encode(aad), tagLength: 128 },
    key,
    bytesToArrayBuffer(cleartext),
  );
  return { version: 1, algorithm: "A256GCM", iv: toBase64Url(iv), ciphertext: toBase64Url(new Uint8Array(ciphertext)) };
}

async function unwrapBytes(key: CryptoKey, envelope: WrappedEnvelope, aad: string): Promise<Uint8Array> {
  const cleartext = await crypto.subtle.decrypt(
    {
      name: "AES-GCM",
      iv: bytesToArrayBuffer(fromBase64Url(envelope.iv)),
      additionalData: textEncoder.encode(aad),
      tagLength: 128,
    },
    key,
    bytesToArrayBuffer(fromBase64Url(envelope.ciphertext)),
  );
  return new Uint8Array(cleartext);
}

function formatRecoveryCode(bytes: Uint8Array): string {
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let bits = 0;
  let value = 0;
  let output = "";
  for (const byte of bytes) {
    value = (value << 8) | byte;
    bits += 8;
    while (bits >= 5) {
      output += alphabet[(value >>> (bits - 5)) & 31];
      bits -= 5;
    }
  }
  return output.match(/.{1,4}/gu)!.join("-");
}

export function unsignedDescriptor(descriptor: SignedDescriptorEvent): DescriptorEvent {
  return descriptorPayload(descriptor);
}
